'use client';

/**
 * Root-level error boundary that catches unhandled errors outside the locale
 * layout (e.g., server action mismatches after a deploy, hydration failures,
 * or any uncaught error above the i18n/theme providers).
 *
 * Lives OUTSIDE the normal React tree — no i18n, no Tailwind classes, no
 * providers. Styling is intentionally inline so nothing here can itself fail.
 *
 * Stale-deployment handling: when a stale client sends a request for a server
 * action hash that no longer exists on the new deployment, Next.js throws
 * "Failed to find Server Action". We auto-reload once to pick up new assets;
 * if that doesn't clear the issue, fall through to the generic fallback.
 *
 * Button semantics — important:
 *   - `reset()` (Next.js built-in) re-renders the segment. For *persistent*
 *     errors (bad server state, broken query, etc.) this is a no-op from the
 *     user's perspective: the same tree renders and throws the same error.
 *     That's why the old version's button "didn't work" — it was calling
 *     reset() on a persistent error.
 *   - `window.location.reload()` is the escape hatch that always produces a
 *     visible effect: fresh page load, fresh state, fresh chance.
 *   - "Go home" gives a definite path out if the current URL is broken.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const isStaleDeployment =
    error.message?.includes('Failed to find Server Action') ||
    error.message?.includes(
      'router state header was sent but could not be parsed'
    );

  if (isStaleDeployment && typeof window !== 'undefined') {
    // Prevent reload loops — only reload once per page load.
    const key = '__lexomat_stale_reload';
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      window.location.reload();
      return null;
    }
    // Already reloaded once and still failing — clear and show fallback UI.
    sessionStorage.removeItem(key);
  }

  const handleRetry = () => {
    // Try reset() first (cheaper, no network). If the error is persistent,
    // the user can click the explicit reload/home buttons.
    try {
      reset();
    } catch {
      // reset() itself threw — fall through to reload.
    }
    // Belt-and-suspenders: after a brief tick, if we're still on this screen,
    // the user will see the Reload and Home buttons — they're always rendered.
  };

  const handleReload = () => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  const handleGoHome = () => {
    if (typeof window !== 'undefined') window.location.href = '/';
  };

  const headingText = isStaleDeployment
    ? 'Nová verzia aplikácie'
    : 'Niečo sa pokazilo';
  const descriptionText = isStaleDeployment
    ? 'Bola nasadená nová verzia Lexomatu. Obnovte stránku pre pokračovanie.'
    : 'Nastala neočakávaná chyba. Skúste to znova alebo sa vráťte na domovskú stránku.';

  return (
    <html lang='sk'>
      <body
        style={{
          margin: 0,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
          background: '#0B0A13',
          color: '#E7E5EA',
        }}
      >
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
          }}
        >
          <div
            style={{
              maxWidth: '480px',
              width: '100%',
              background: '#14131D',
              border: '1px solid #27262F',
              borderRadius: '1rem',
              padding: '2.5rem 2rem',
              textAlign: 'center',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
            }}
          >
            {/* Warning triangle icon (inline SVG — no dependency on lucide) */}
            <div
              style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 1.25rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '9999px',
                background: 'rgba(239, 68, 68, 0.1)',
              }}
              aria-hidden='true'
            >
              <svg
                width='32'
                height='32'
                viewBox='0 0 24 24'
                fill='none'
                stroke='#EF4444'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z' />
                <line x1='12' y1='9' x2='12' y2='13' />
                <line x1='12' y1='17' x2='12.01' y2='17' />
              </svg>
            </div>

            <h1
              style={{
                fontSize: '1.375rem',
                fontWeight: 600,
                margin: '0 0 0.5rem',
                color: '#F5F4F7',
              }}
            >
              {headingText}
            </h1>

            <p
              style={{
                fontSize: '0.9375rem',
                lineHeight: 1.55,
                color: '#A19EAA',
                margin: '0 0 1.75rem',
              }}
            >
              {descriptionText}
            </p>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              {/* Primary: reload (always works, most reliable) */}
              <button
                onClick={isStaleDeployment ? handleReload : handleRetry}
                style={{
                  padding: '0.625rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background:
                    'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                  color: '#FFFFFF',
                  fontSize: '0.9375rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  minHeight: '44px',
                  transition: 'opacity 150ms ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                {isStaleDeployment ? 'Obnoviť stránku' : 'Skúsiť znova'}
              </button>

              {/* Escape hatches — always rendered so the user never gets stuck */}
              {!isStaleDeployment && (
                <button
                  onClick={handleReload}
                  style={{
                    padding: '0.625rem 1.5rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #27262F',
                    background: 'transparent',
                    color: '#E7E5EA',
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    minHeight: '44px',
                    transition: 'background 150ms ease',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = '#1C1B26')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'transparent')
                  }
                >
                  Obnoviť stránku
                </button>
              )}

              <button
                onClick={handleGoHome}
                style={{
                  padding: '0.625rem 1.5rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  background: 'transparent',
                  color: '#A19EAA',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  minHeight: '44px',
                  transition: 'color 150ms ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#E7E5EA')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#A19EAA')}
              >
                Prejsť na úvod
              </button>
            </div>

            {/* Error digest for support diagnostics */}
            {error.digest && (
              <p
                style={{
                  marginTop: '1.5rem',
                  fontSize: '0.75rem',
                  color: '#6B6879',
                  fontFamily:
                    'ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace',
                }}
              >
                {error.digest}
              </p>
            )}
          </div>
        </div>
      </body>
    </html>
  );
}
